// Brush collision data for BspWorld, as built by the BSP worker (src/maps/bsp/buildMap.ts).
// Plain typed arrays so it can be transferred from the worker without copying.

type V3 = [number, number, number];

/** A world or brush entity that has collision. Index 0 is always the world. */
export interface SolidInfo {
  classname: string;
  /** Index into MapData.entities, -1 for the world. */
  entity: number;
  model: number;
  origin: V3;
  /** False for solids that start switched off (func_wall_toggle hidden, disabled triggers...). Default true. */
  enabled?: boolean;
}

export interface CollisionData {
  /** 4 numbers per brush side: normal x, y, z and dist, in the solid's local space. */
  planes: Float64Array;
  /** 4 ints per brush: first side, side count, contents, solid index. */
  brushes: Int32Array;
  /** 6 numbers per brush: local mins xyz, maxs xyz. */
  bounds: Float64Array;
  solids: SolidInfo[];
  /** Uniform 2D (XY) grid over brush bounds, compressed rows: brushes of cell c are items[cellStart[c]..cellStart[c+1]). */
  grid: {
    originX: number;
    originY: number;
    cellSize: number;
    nx: number;
    ny: number;
    cellStart: Int32Array;
    items: Int32Array;
  };
}

/** Default grid cell size in units. Bhop maps are mostly horizontal, so a 2D grid over XY works well. */
export const GRID_CELL = 256;

/** Bucket brushes into a uniform XY grid by their world-space bounds (local bounds + solid origin). */
export function buildBrushGrid(
  brushes: Int32Array,
  bounds: Float64Array,
  solids: readonly SolidInfo[],
  cellSize = GRID_CELL,
  /** Only bucket brushes for which this returns true (others are tested separately). */
  include: (brush: number) => boolean = () => true,
): CollisionData['grid'] {
  const nb = brushes.length / 4;
  let gx0 = Infinity;
  let gy0 = Infinity;
  let gx1 = -Infinity;
  let gy1 = -Infinity;
  for (let i = 0; i < nb; i++) {
    if (!include(i)) continue;
    const o = solids[brushes[i * 4 + 3]].origin;
    gx0 = Math.min(gx0, bounds[i * 6] + o[0]);
    gy0 = Math.min(gy0, bounds[i * 6 + 1] + o[1]);
    gx1 = Math.max(gx1, bounds[i * 6 + 3] + o[0]);
    gy1 = Math.max(gy1, bounds[i * 6 + 4] + o[1]);
  }
  if (gx0 > gx1) gx0 = gy0 = gx1 = gy1 = 0;
  const nx = Math.max(1, Math.ceil((gx1 - gx0) / cellSize) + 1);
  const ny = Math.max(1, Math.ceil((gy1 - gy0) / cellSize) + 1);
  const range = new Int32Array(4);
  const cellRange = (i: number) => {
    const o = solids[brushes[i * 4 + 3]].origin;
    range[0] = Math.max(0, Math.floor((bounds[i * 6] + o[0] - gx0) / cellSize));
    range[1] = Math.max(0, Math.floor((bounds[i * 6 + 1] + o[1] - gy0) / cellSize));
    range[2] = Math.min(nx - 1, Math.floor((bounds[i * 6 + 3] + o[0] - gx0) / cellSize));
    range[3] = Math.min(ny - 1, Math.floor((bounds[i * 6 + 4] + o[1] - gy0) / cellSize));
    return range;
  };
  const cellStart = new Int32Array(nx * ny + 1);
  for (let i = 0; i < nb; i++) {
    if (!include(i)) continue;
    const [cx0, cy0, cx1, cy1] = cellRange(i);
    for (let y = cy0; y <= cy1; y++) for (let x = cx0; x <= cx1; x++) cellStart[y * nx + x + 1]++;
  }
  for (let c = 1; c < cellStart.length; c++) cellStart[c] += cellStart[c - 1];
  const items = new Int32Array(cellStart[nx * ny]);
  const fill = cellStart.slice();
  for (let i = 0; i < nb; i++) {
    if (!include(i)) continue;
    const [cx0, cy0, cx1, cy1] = cellRange(i);
    for (let y = cy0; y <= cy1; y++) for (let x = cx0; x <= cx1; x++) items[fill[y * nx + x]++] = i;
  }
  return { originX: gx0, originY: gy0, cellSize, nx, ny, cellStart, items };
}

/**
 * Collision data for axis-aligned boxes (6 axial planes each), all part of the world solid.
 * Used by tests and handy for procedural content.
 */
export function boxesToCollision(
  boxes: readonly { min: readonly number[]; max: readonly number[]; contents?: number }[],
): CollisionData {
  const planes = new Float64Array(boxes.length * 24);
  const brushes = new Int32Array(boxes.length * 4);
  const bounds = new Float64Array(boxes.length * 6);
  boxes.forEach((b, i) => {
    const p = [
      [1, 0, 0, b.max[0]],
      [-1, 0, 0, -b.min[0]],
      [0, 1, 0, b.max[1]],
      [0, -1, 0, -b.min[1]],
      [0, 0, 1, b.max[2]],
      [0, 0, -1, -b.min[2]],
    ];
    planes.set(p.flat(), i * 24);
    brushes.set([i * 6, 6, b.contents ?? 1, 0], i * 4);
    bounds.set([...b.min, ...b.max], i * 6);
  });
  const solids: SolidInfo[] = [{ classname: 'worldspawn', entity: -1, model: 0, origin: [0, 0, 0] }];
  return { planes, brushes, bounds, solids, grid: buildBrushGrid(brushes, bounds, solids) };
}
